/* eslint-disable @typescript-eslint/naming-convention */
export { equals as arrayEquals, matches as arrayMatches, containsAll, containsAny, difference, disjunction, intersection, isEmptyArray, isNonEmptyArray, range, requireArray, union } from './his-array.utils';
export { clamp, isInRange, modulo, snapTo, wrap } from './his-number.utils';
export { entries, isEmptyObject, isNonEmptyObject, isObject, isPlainObject, keys, nonEmptyObject, values } from './his-object.utils';
export { ensurePrefix, ensureSuffix, escapeRegExp, isBlank, isString, removePrefix, removeSuffix, splitX, trimToEmpty, trimToNull, trimToUndefined } from './his-string.utils';
export { HisContentMatcherConfiguration } from './his-test.utils';
export * from './his-value.utils';

export type * from './his-type.utils';

import * as arrayUtils from './his-array.utils';
import * as conversionUtils from './his-conversion.utils';
import * as dateUtils from './his-date.utils';
import * as enumUtils from './his-enum.utils';
import * as formUtils from './his-form.utils';
import * as htmlUtils from './his-html.utils';
import * as mapUtils from './his-map.utils';
import * as numberUtils from './his-number.utils';
import * as objectUtils from './his-object.utils';
import * as stringUtils from './his-string.utils';
import * as testUtils from './his-test.utils';
import * as urlUtils from './his-url.utils';
import * as valueUtils from './his-value.utils';

/**
 * # HisArrayUtils
 * Utility functions for working with arrays, including validation, comparison, and manipulation.
 * 
 * ## 1) Array validation
 * - `isEmptyArray`: Checks if a value is an empty array.
 * - `isNonEmptyArray`: Checks if a value is a non-empty array.
 * - `requireArray`: Asserts that a value is an array, throwing an error if not.
 * ```ts
 * const arr = [1, 2, 3];
 * isEmptyArray(null);   // false
 * isEmptyArray(arr);    // false
 * isEmptyArray([]);     // true
 * isNonEmptyArray(arr); // true
 * // 'arr' is now of type NonEmpty<number[]>, which is defined as having at least one element
 * 
 * requireArray(arr);    // [1, 2, 3]
 * requireArray(null);   // throws Error: Value must be an array
 * // All functions retain/enrich type information if available
 * ```
 * 
 * ## 2) Array comparison
 * All comparison functions accept an optional `compareFn` parameter, which is a function that takes
 * two elements and returns a boolean indicating whether they are considered equal. If no `compareFn`
 * is provided, strict equality (`===`) is used.
 * 
 * ### a) Conditional comparison
 * - `matches`: Checks if two arrays are equal (ignoring order), using an optional comparison function.
 * - `equals`: Checks if two arrays are strictly equal, using an optional comparison function.
 * - `containsAll`: Checks if an array contains all elements of another array.
 * - `containsAny`: Checks if an array contains any elements of another array.
 * ```ts
 * const a = [1, 2, 3];
 * const b = [3, 2, 1];
 * const c = [3, 4, 5];
 * containsAny(a, c);    // true  // 3 is in both arrays
 * containsAll(a, c);    // false // not all elements of c are in a
 * containsAll(a, [3]);  // true  // 3 is in a; [3] is a subset of a
 * containsAll(a, []);   // true  // empty array is a subset of any array
 * matches(a, b);        // true  // same elements, different order
 * equals(a, b);         // false // different order
 * ```
 * 
 * ### b) Set logic
 * - `difference`: Returns the elements in one array that are not in another. (A ∖ B)
 * - `intersection`: Returns the common elements between two arrays. (A ∩ B)
 * - `union`: Returns a new array containing all unique elements from two arrays. (A ∪ B)
 * - `disjunction`: Returns the elements that are in either of the two arrays but not in both. (A △ B)
 * ```ts
 * const a = [1, 2, 3];
 * const b = [3, 4, 5];
 * difference(a, b);   // [1, 2]          // 3 is in both arrays, so it's excluded
 * intersection(a, b); // [3]             // only 3 is common to both arrays
 * union(a, b);        // [1, 2, 3, 4, 5] // all unique elements from both arrays
 * disjunction(a, b);  // [1, 2, 4, 5]    // elements in either array but not in both
 * ```
 * 
 * ## 3) Array manipulation/creation
 * - `padRecursive`: Pads an array to a specified length by recursively adding elements.
 * - `range`: Creates an array of numbers within a specified range.
 */
export const HisArrayUtils = arrayUtils;
export const HisConversionUtils = conversionUtils;
export const HisDateUtils = dateUtils;
export const HisEnumUtils = enumUtils;
export const HisFormUtils = formUtils;
export const HisHtmlUtils = htmlUtils;
export const HisMapUtils = mapUtils;
/**
 * # HisNumberUtils
 * Utility functions for working with numbers, including validation, manipulation, and formatting.
 * 
 * ## 1) Number validation
 * - `isInRange`: Checks if a number is within a specified range, with optional inclusivity.
 * 
 * ## 2) Number manipulation
 * - `clamp`: Clamps a number between a minimum and maximum range.
 * - `wrap`: Wraps a number within a specified range, cycling rather than clamping.
 * - `modulo`: Performs a modulo operation, with options for euclidean or absolute remainder.
 *   - `absoluteModulo`: Returns the absolute remainder of a division operation.
 *   - `euclideanModulo`: Returns the positive euclidean remainder of a division operation.
 * - `getHashCode`: Generates a hash code for a given string representation of a number.
 * - `toInteger`: Converts a value to an integer, truncating any decimal portion.
 * - `snapTo`: Snaps a number to the nearest multiple of a specified step, with optional rounding mode.
 */
export const HisNumberUtils = numberUtils;
/**
 * # HisObjectUtils
 * Utility functions for working with objects, including validation, manipulation, and formatting.
 * 
 * ## 1) Object validation
 * - `isObject`: Checks if a value is an object (excluding null).
 * - `isPlainObject`: Checks if a value is a plain object (created by `{}` or `new Object()`).
 * - `isEmptyObject`: Checks if an object has no enumerable properties.
 * - `isNonEmptyObject`: Checks if an object has at least one enumerable property.
 * 
 * ## 2) Object manipulation
 * - `nonEmptyObject`: Optional style nullish coalescing for objects. Returns the object if it is non-empty,
 *    otherwise returns a custom fallback value.
 * - `cleanObject`: Removes properties with null or undefined values from an object.
 * - `defineDefaults`: Merges default values into an object, without overwriting existing properties. Guarantees
 *    the presence of the default properties in the resulting object, though not necessarily with their values.
 * 
 * ## 3) Object introspection
 * - `entries`: `Object.entries` with stronger typing
 * - `keys`: `Object.keys` with stronger typing
 * - `values`: `Object.values` with stronger typing
 */
export const HisObjectUtils = objectUtils;
/**
 * # HisStringUtils
 * Utility functions for working with strings, including validation, manipulation, and formatting.
 * 
 * ## 1) String validation
 * - `isString`: Checks if a value is a string.
 * - `isBlank`: Checks if a string is null, undefined, or consists only of whitespace.
 * 
 * ## 2) String manipulation
 * - `trimToNull`: Trims a string and returns null if the result is empty.
 * - `trimToUndefined`: Trims a string and returns undefined if the result is empty.
 * - `trimToEmpty`: Trims a string and returns an empty string if the result is empty.
 * - `trimAll`: Removes excess whitespace from a string, including leading/trailing whitespace and multiple consecutive spaces.
 * - `splitTrimmed`: Splits a string by a specified separator and trims each resulting substring.
 * - `splitX`: Splits a string by a specified separator, respecting nested brackets or quotes and ignoring separators within them.
 * - `joinNonBlank`: Joins an array of strings into a single string, ignoring null or undefined values.
 * - `normalizeLineEndings`: Normalizes line endings in a string to a specified format (default is '\n').
 * - `escapeRegExp`: Escapes special characters in a string for use in a regular expression.
 * 
 * ## 3) String prefix/suffix handling
 * - `removePrefix`: Removes a specified prefix from a string, if present.
 * - `removeSuffix`: Removes a specified suffix from a string, if present.
 * - `ensurePrefix`: Adds a specified prefix to a string, if not already present.
 * - `ensureSuffix`: Adds a specified suffix to a string, if not already present.
 */
export const HisStringUtils = stringUtils;
export const HisTestUtils = testUtils;
export const HisUrlUtils = urlUtils;
/**
 * # HisValueUtils
 * Shared value-validation primitives used across the Angular extensions.
 *
 * ## 1) Hard assertions
 * Use these when invalid input is a programming/runtime error and should fail fast (and documented).
 *
 * These utils encourage the semantic difference between `null` and `undefined`:
 * - `null`: The value is deliberately not set. It is _defined_ as _not present_.
 * - `undefined`: Like the name suggests, this is a value that is _not defined_, meaning it could be
 * missing or was forgotten or got lost unexpectedly.
 * ```ts
 * requireNonNil( userId, 'userId is required' );
 * requireDefined( optionalField );
 * requireTrue( count > 0, 'count must be > 0' );
 * requireFalse( isReadonly( prop ), 'prop must be writable' );
 *
 * // generic assertion with custom predicate/type guard
 * requireValue( value, v => typeof v === 'string' && v.length > 0, 'non-empty string expected' );
 * ```
 *
 * ## 2) Requirement adapters
 * Wrap an existing requirement and allow selected nil values before evaluating the original rule.
 *
 * ```ts
 * const isPositive = ( n: any ) => typeof n === 'number' && n > 0;
 *
 * const isPositiveOrNil = allowNil( isPositive );
 * isPositiveOrNil( null ); // true
 * isPositiveOrNil( 5 );    // true
 *
 * const isPositiveOrNull = allowNull( isPositive );
 * isPositiveOrNull( undefined ); // false
 *
 * const isPositiveOrUndefined = allowUndefined( isPositive );
 * isPositiveOrUndefined( undefined ); // true
 * ```
 *
 * ## 3) Requirement combinators
 * Compose predicates into reusable validation rules for _stream_ operations like `filter` or `map`
 * or the above mentioned `requireValue` assertion guard.
 *
 * ```ts
 * const isString = ( v: any ) => typeof v === 'string';
 * const isNotBlank = ( v: any ) => v.trim().length > 0;
 * const startsWithA = ( v: any ) => isString( v ) && v.startsWith( 'A' );
 * 
 * and( isString, isNotBlank )( '  ' );       // false
 * or( startsWithA, isNotBlank )( 'Alice' );  // true
 * none( startsWithA, isNotBlank )( '' );     // true
 * not( startsWithA )( 'Bob' );               // true
 * xor( startsWithA, isNotBlank )( 'Alice' ); // false (both true)
 * // Usually these would be passed as predicate functions without explicitly calling them: arr.filter( not( startsWithA ) );
 * ```
 *
 * ## 4) Soft checks
 * Lightweight predicates for control flow and _stream_ operations.
 *
 * ```ts
 * isNil( null );          // true
 * isNotNil( 'x' );        // true
 * isDefined( null );      // true  (only undefined is rejected)
 * isNotDefined( void 0 ); // true
 * isNull( null );         // true
 * isNotNull( 'x' );       // true
 * isTrue( true );         // true
 * isFalse( false );       // true
 * isTruthy( 1 );          // true
 * isFalsy( '' );          // true
 *
 * // practical stream usage
 * [ 'A', null, 'B', undefined ].filter( isNotNil ); // ['A', 'B']
 * ```
 * 
 * ## 5) External API extensions
 * Other utils may define their own requirements and soft checks that are specific to their domain.
 * 
 * For example, the `HisArrayUtils` module defines their own `isEmptyArray` and `isNonEmptyArray` predicates,
 * which can be used in conjunction with the above combinators.
 * 
 * These custom predicates should follow this module's naming convention with `requireX` and `isX` prefixes,
 * and should be exported from their respective modules.
 */
export const HisValueUtils = valueUtils;